// src/components/Atelier3/Activite1/Activite1.js
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { motion } from "framer-motion";
import {
  Save,
  Plus,
  Info,
  Users,
  BarChart2,
  Table,
  Filter,
  Settings,
  Download,
  FileText,
  ChevronDown,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import useAtelier3Store from '../../../stores/useAtelier3Store';
import { useBroadcast } from '../../../hooks/useBroadcast';
import StakeholderForm from "./StakeholderForm";
import StakeholderTable from "./StakeholderTable";
import StakeholderSummaryTable from "./StakeholderSummaryTable";
import ThreatRadarVisualization from "./ThreatRadarVisualization";
import CategoryManager from "./CategoryManager";
import GuideModal from "./GuideModal";
import ReactDOM from "react-dom";
import {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  updateToast,
} from "../../../utils/toastUtils";
import { createIdGenerator } from "../../../utils/idGenerator";
import WorkshopExportMenu from "../../common/WorkshopExportMenu";
import Button from "../../common/Button";
import { useSafeSave } from "../../../hooks/useSafeSave";
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import Workshop3Activite1CsvGenerator from "../../../utils/workshop3Activite1CsvGenerator";

// Get color based on stakeholder category
const getCategoryColor = (category) => {
  switch (category) {
    case "client":
      return "#3B82F6"; // blue
    case "partner":
      return "#10B981"; // green
    case "provider":
      return "#F59E0B"; // amber
    case "technical":
      return "#8B5CF6"; // purple
    case "business":
      return "#EC4899"; // pink
    case "subsidiary":
      return "#6366F1"; // indigo
    default:
      return "#6B7280"; // gray
  }
};

const Activite1 = (props) => {
  const { t } = useTranslation();
  const stakeholders = useAtelier3Store((s) => s.stakeholders);

  // Category entries live in the same array, flagged with isCategory
  const stakeholderCategories = useMemo(
    () => (stakeholders || []).filter((s) => s.isCategory && !s.deleted),
    [stakeholders],
  );
  const partPrenantes = useMemo(
    () => (stakeholders || []).filter((s) => !s.isCategory),
    [stakeholders],
  );
  const categoryNamesById = useMemo(
    () => Object.fromEntries(stakeholderCategories.map((c) => [c.id, c.name])),
    [stakeholderCategories],
  );
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isThresholdEditing, setIsThresholdEditing] = useState(false);
  const [thresholds, setThresholds] = useState({
    danger: 3,
    control: 1.5,
    watch: 0.5,
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [filterType, setFilterType] = useState("all"); // 'all', 'internal', 'external'
  const [filterCategory, setFilterCategory] = useState("all"); // 'all', 'client', 'partner', etc.
  const threatRadarRef = useRef(null);

  // Get analysis context
  const analysisStore = useAnalysisStore();
  const atelier1Store = useAtelier1Store();
  const atelier3Store = useAtelier3Store();
  const broadcastUpdate = useBroadcast();
  const currentAnalysis = analysisStore.currentAnalysis;
  const currentContextData = atelier1Store.contextData;
  const getStakeholders = atelier3Store.loadStakeholders;
  const getStakeholderThresholds = atelier3Store.getStakeholderThresholds;
  const updateStakeholderThresholds = atelier3Store.updateStakeholderThresholds;

  const {
    markModified,
    canAutoSave,
    clearModified,
    useFormChangeDetector,
  } = useSafeSave("Atelier3Activite1");
  const formChangeRef = useFormChangeDetector();

  // Handle silent save without toasts
  const handleSilentSave = async () => {
    if (!canAutoSave()) return "skipped";

    if (currentAnalysis?.id && hasUnsavedChanges) {
      try {
        console.log("Activite1 - Performing silent save of stakeholders");

        let radarImageData = null;
        if (
          threatRadarRef.current &&
          threatRadarRef.current.generateVisualizationImage
        ) {
          try {
            radarImageData =
              await threatRadarRef.current.generateVisualizationImage();
          } catch (e) {
            console.warn("Silent save visualization capture failed");
          }
        }

        const response = await useAtelier3Store.getState().saveStakeholders(
          currentAnalysis.id,
          stakeholders,
          radarImageData,
          broadcastUpdate,
        );
        if (!response.success) {
          console.error("Activite1 - Silent save failed:", response.message);
          return false;
        }
        setHasUnsavedChanges(false);
        clearModified();
        console.log("Activite1 - Silent save successful");
        return true;
      } catch (error) {
        console.error("Activite1 - Silent save error:", error);
        return false;
      }
    }
    return false;
  };

  useSilentSaveListener('atelier3-activite1', handleSilentSave);

  // Create ID generator for this analysis
  const idGen = currentAnalysis?.id
    ? createIdGenerator({ analysisId: currentAnalysis.id })
    : null;

  // Load thresholds when component mounts (stakeholders come from store via subscription)
  useEffect(() => {
    if (!currentAnalysis?.id) return;
    getStakeholderThresholds(currentAnalysis.id).then((res) => {
      if (res?.data) setThresholds(res.data);
    });
  }, [currentAnalysis?.id]);

  // Set up global capture function for PDF reports
  useEffect(() => {
    // Set up global function for PDF report generation
    window.captureStakeholderRadarVisualization = async () => {
      if (
        threatRadarRef.current &&
        threatRadarRef.current.generateVisualizationImage
      ) {
        console.log(
          "📸 Global capture function called for stakeholder radar visualization...",
        );
        try {
          const imageData =
            await threatRadarRef.current.generateVisualizationImage();
          console.log(
            "✅ Global stakeholder radar visualization captured successfully",
          );
          return imageData;
        } catch (error) {
          console.error("❌ Global stakeholder radar capture failed:", error);
          throw error;
        }
      } else {
        console.warn(
          "⚠️ Stakeholder radar ref or capture function not available",
        );
        throw new Error("Stakeholder radar visualization not available");
      }
    };

    // Cleanup function
    return () => {
      if (window.captureStakeholderRadarVisualization) {
        delete window.captureStakeholderRadarVisualization;
      }
    };
  }, [threatRadarRef.current]); // Re-setup when ref changes

  // Handle save button click
  const handleSave = async () => {
    if (!currentAnalysis?.id) {
      showErrorToast(t("workshop2.activity1.errors.noAnalysisSelected"));
      return;
    }

    // Show loading toast
    const toastId = showLoadingToast(t("workshop3.activity1.saving"));

    try {
      // Capture radar visualization if available
      let radarImageData = null;
      if (
        threatRadarRef.current &&
        threatRadarRef.current.generateVisualizationImage
      ) {
        try {
          console.log("📸 Capturing stakeholder radar visualization...");
          radarImageData =
            await threatRadarRef.current.generateVisualizationImage();
          console.log(
            "✅ Stakeholder radar visualization captured successfully",
          );
        } catch (captureError) {
          console.warn(
            "⚠️ Failed to capture stakeholder radar visualization:",
            captureError,
          );
          // Continue with save even if capture fails
        }
      } else {
        console.log("ℹ️ No visualization capture function available");
      }

      const response = await useAtelier3Store.getState().saveStakeholders(
        currentAnalysis.id,
        stakeholders,
        radarImageData,
        broadcastUpdate,
      );

      if (response.success) {
        const successMessage = radarImageData
          ? t("workshop3.activity1.success.stakeholdersSavedWithRadar")
          : t("workshop3.activity1.success.stakeholdersSaved");
        updateToast(toastId, successMessage, "success");
        setHasUnsavedChanges(false);
        clearModified();
      } else {
        updateToast(
          toastId,
          t("workshop3.activity1.errors.saveError", {
            message:
              response.message || t("workshop3.activity1.errors.unknownError"),
          }),
          "error",
        );
      }
    } catch (error) {
      console.error("Error saving stakeholders:", error);
      updateToast(
        toastId,
        t("workshop3.activity1.errors.saveStakeholdersError"),
        "error",
      );
    }
  };

  // Handle threshold update
  const handleUpdateThresholds = async (newThresholds) => {
    if (!currentAnalysis?.id) {
      showErrorToast(t("workshop2.activity1.errors.noAnalysisSelected"));
      return;
    }

    // Show loading toast
    const toastId = showLoadingToast(
      t("workshop3.activity1.updatingThresholds"),
    );

    try {
      const response = await updateStakeholderThresholds(
        currentAnalysis.id,
        newThresholds,
      );

      if (response.success) {
        setThresholds(newThresholds);
        updateToast(
          toastId,
          t("workshop3.activity1.success.thresholdsUpdated"),
          "success",
        );
      } else {
        updateToast(
          toastId,
          t("workshop3.activity1.errors.saveError", {
            message:
              response.message || t("workshop3.activity1.errors.unknownError"),
          }),
          "error",
        );
      }
    } catch (error) {
      console.error("Error updating thresholds:", error);
      updateToast(
        toastId,
        t("workshop3.activity1.errors.updateThresholdsError"),
        "error",
      );
    }
  };

  // Handle stakeholder creation/update
  const handleStakeholderSubmit = (stakeholderData) => {
    let updatedStakeholdersBase;
    // Handle "Other" category: create the category first, then assign its id
    if (stakeholderData.newCategoryName) {
      const newCatName = String(stakeholderData.newCategoryName).trim();
      if (!newCatName) {
        showErrorToast(
          t("workshop3.activity1.categoryManager.errors.noName", "Veuillez saisir un nom de catégorie"),
        );
        return;
      }
      let baseList = stakeholders;
      const exists = baseList.some(
        (s) => s.isCategory && String(s.name || '').toLowerCase() === newCatName.toLowerCase(),
      );
      if (!exists) {
        const newCatId = idGen
          ? idGen.generateNext("stakeholder", baseList)
          : `cat-${Date.now()}`;
        const now = new Date();
        baseList = [
          ...baseList,
          {
            id: newCatId,
            name: newCatName,
            isCategory: true,
            builtin: false,
            createdAt: now,
            updatedAt: now,
          },
        ];
      }
      const catEntry = baseList.find(
        (s) => s.isCategory && String(s.name || '').toLowerCase() === newCatName.toLowerCase(),
      );
      stakeholderData = {
        ...stakeholderData,
        category: catEntry ? catEntry.id : stakeholderData.category,
      };
      delete stakeholderData.newCategoryName;
      updatedStakeholdersBase = baseList;
    }

    let updatedStakeholders;
    let baseList = updatedStakeholdersBase || stakeholders;
    if (selectedStakeholder) {
      updatedStakeholders = baseList.map((s) =>
        s.id === selectedStakeholder.id
          ? { ...s, ...stakeholderData, updatedAt: new Date() }
          : s,
      );
      showSuccessToast(t("workshop3.activity1.success.stakeholderUpdated"));
    } else {
      const newStakeholder = {
        ...stakeholderData,
        id: idGen
          ? idGen.generateNext("stakeholder", baseList)
          : Date.now().toString(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      updatedStakeholders = [...baseList, newStakeholder];
      showSuccessToast(t("workshop3.activity1.success.stakeholderAdded"));
    }

    useAtelier3Store.setState({ stakeholders: updatedStakeholders });
    setSelectedStakeholder(null);
    setIsFormOpen(false);
    setHasUnsavedChanges(true);
    markModified();
  };

  // Handle stakeholder deletion
  const handleDeleteStakeholder = (id) => {
    // Find the stakeholder to get its name for the toast message
    const stakeholderToDelete = stakeholders.find((s) => s.id === id);
    const stakeholderName =
      stakeholderToDelete?.name || t("workshop3.activity1.stakeholder");

    useAtelier3Store.setState({
      stakeholders: stakeholders.filter((s) => s.id !== id),
    });
    setHasUnsavedChanges(true);
    markModified();
    showSuccessToast(
      t("workshop3.activity1.success.stakeholderDeleted", {
        name: stakeholderName,
      }),
    );
  };

  // Category handlers (built-in + custom, stored as isCategory entries)
  const handleAddCategory = (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) {
      showErrorToast(
        t("workshop3.activity1.categoryManager.errors.noName", "Veuillez saisir un nom de catégorie"),
      );
      return;
    }
    if (
      stakeholderCategories.some(
        (c) => String(c.name || "").toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      showErrorToast(
        t("workshop3.activity1.categoryManager.errors.duplicate", "Une catégorie porte déjà ce nom"),
      );
      return;
    }
    const id = idGen ? idGen.generateNext("stakeholder", stakeholders) : `cat-${Date.now()}`;
    const now = new Date();
    useAtelier3Store.setState({
      stakeholders: [
        ...stakeholders,
        { id, name: trimmed, isCategory: true, builtin: false, createdAt: now, updatedAt: now },
      ],
    });
    setHasUnsavedChanges(true);
    markModified();
    showSuccessToast(
      t("workshop3.activity1.success.stakeholderAdded", "Catégorie ajoutée"),
    );
  };

  const handleRenameCategory = (id, newName) => {
    const trimmed = String(newName || "").trim();
    if (!trimmed) return;
    const duplicate = stakeholderCategories.some(
      (c) => c.id !== id && String(c.name || "").toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) {
      showErrorToast(
        t("workshop3.activity1.categoryManager.errors.duplicate", "Une catégorie porte déjà ce nom"),
      );
      return;
    }
    useAtelier3Store.setState({
      stakeholders: stakeholders.map((s) =>
        s.id === id ? { ...s, name: trimmed, updatedAt: new Date() } : s,
      ),
    });
    setHasUnsavedChanges(true);
    markModified();
  };

  const handleDeleteCategory = (id, reassignToId) => {
    const target = reassignToId || "unsorted";
    let baseList = stakeholders;
    // Ensure the "unsorted" category exists before reassigning to it
    if (target === "unsorted" && !baseList.some((s) => s.id === "unsorted")) {
      const now = new Date();
      baseList = [
        ...baseList,
        {
          id: "unsorted",
          name: t("workshop3.activity1.categoryManager.unsorted"),
          isCategory: true,
          builtin: true,
          createdAt: now,
          updatedAt: now,
        },
      ];
    }
    useAtelier3Store.setState({
      stakeholders: baseList
        .map((s) => {
          if (s.id === id) return null;
          if (!s.isCategory && s.category === id) {
            return { ...s, category: target, updatedAt: new Date() };
          }
          return s;
        })
        .filter(Boolean),
    });
    setHasUnsavedChanges(true);
    markModified();
    showSuccessToast(
      t("workshop3.activity1.categoryManager.deleted", "Catégorie supprimée"),
    );
  };

  // Handle toggling the retained status of a stakeholder
  const handleToggleRetained = (id) => {
    const updatedStakeholders = stakeholders.map((stakeholder) => {
      if (stakeholder.id === id) {
        const newRetainedStatus = !stakeholder.retained;
        const statusText = newRetainedStatus
          ? t("workshop3.activity1.retained")
          : t("workshop3.activity1.notRetained");
        showSuccessToast(
          t("workshop3.activity1.success.stakeholderStatusChanged", {
            name: stakeholder.name,
            status: statusText,
          }),
        );
        return {
          ...stakeholder,
          retained: newRetainedStatus,
          updatedAt: new Date(),
        };
      }
      return stakeholder;
    });

    useAtelier3Store.setState({ stakeholders: updatedStakeholders });
    setHasUnsavedChanges(true);
    markModified();
  };

  // Handle toggling the critical status of a stakeholder
  const handleToggleCritical = (id) => {
    const updatedStakeholders = stakeholders.map((stakeholder) => {
      if (stakeholder.id === id) {
        const newCritical = !stakeholder.critical;
        const statusText = newCritical
          ? t("workshop3.activity1.critical")
          : t("workshop3.activity1.notCritical");
        showSuccessToast(
          t("workshop3.activity1.success.stakeholderStatusChanged", {
            name: stakeholder.name,
            status: statusText,
          }),
        );
        return {
          ...stakeholder,
          critical: newCritical,
          criticalJustification: newCritical ? (stakeholder.criticalJustification || '') : '',
          updatedAt: new Date(),
        };
      }
      return stakeholder;
    });
    useAtelier3Store.setState({ stakeholders: updatedStakeholders });
    setHasUnsavedChanges(true);
    markModified();
  };

  // Handle updating the critical justification text
  const handleUpdateCriticalJustification = (id, text) => {
    const updatedStakeholders = stakeholders.map((stakeholder) => {
      if (stakeholder.id === id) {
        return { ...stakeholder, criticalJustification: text, updatedAt: new Date() };
      }
      return stakeholder;
    });
    useAtelier3Store.setState({ stakeholders: updatedStakeholders });
    setHasUnsavedChanges(true);
    markModified();
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    try {
      const reportData = {
        analysisId: currentAnalysis?.id,
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalStakeholders: partPrenantes.length,
        retainedStakeholders: partPrenantes.filter((s) => s.retained).length,
        stakeholders: partPrenantes,
        thresholds: thresholds,
      };

      // Import PDF generator dynamically
      const { default: EcosystemPdfGenerator } =
        await import("../../../utils/ecosystemPdfGenerator.js");

      // Create PDF generator instance
      const pdfGenerator = new EcosystemPdfGenerator(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      });

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateEcosystemReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EBIOS-RM_Ecosysteme_${currentContextData?.organizationName || "Organisation"}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast("Rapport PDF généré avec succès");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      showErrorToast(`Erreur lors de l'export PDF: ${error.message}`);
    }
  };

  // Handle English PDF export
  const handleExportPDFEn = async () => {
    try {
      const reportData = {
        analysisId: currentAnalysis?.id,
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalStakeholders: partPrenantes.length,
        retainedStakeholders: partPrenantes.filter((s) => s.retained).length,
        stakeholders: partPrenantes,
        thresholds: thresholds,
      };

      // Import bilingual PDF generator (EN mode)
      const { default: EcosystemPdfGenerator } =
        await import("../../../utils/ecosystemPdfGenerator.js");

      // Create PDF generator instance
      const pdfGenerator = new EcosystemPdfGenerator(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
        createdBy: "Système EBIOS RM",
      }, "en");

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateEcosystemReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EBIOS-RM_Ecosystem_Mapping_${currentContextData?.organizationName || "Organization"}_${new Date().toISOString().slice(0, 10)}_EN.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast("PDF Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting PDF (EN):", error);
      showErrorToast(`Error exporting PDF (EN): ${error.message}`);
    }
  };

  // Handle DOCX export
  const handleExportDocx = async () => {
    try {
      const reportData = {
        analysisId: currentAnalysis?.id,
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalStakeholders: partPrenantes.length,
        retainedStakeholders: partPrenantes.filter((s) => s.retained).length,
        stakeholders: partPrenantes,
        thresholds: thresholds,
      };

      // Import DOCX generator dynamically
      const { generateEcosystemDocx } =
        await import("../../../utils/ecosystemDocxGenerator.js");

      await generateEcosystemDocx(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
      });

      showSuccessToast("Rapport Word généré avec succès");
    } catch (error) {
      console.error("Error exporting DOCX:", error);
      showErrorToast(`Erreur lors de l'export Word: ${error.message}`);
    }
  };

  // Handle English DOCX export
  const handleExportDocxEn = async () => {
    try {
      const reportData = {
        analysisId: currentAnalysis?.id,
        analysisName: currentAnalysis?.name || "Analyse EBIOS RM",
        organizationName:
          currentContextData?.organizationName || "Organisation",
        totalStakeholders: partPrenantes.length,
        retainedStakeholders: partPrenantes.filter((s) => s.retained).length,
        stakeholders: partPrenantes,
        thresholds: thresholds,
      };

      // Import DOCX generator dynamically
      const { generateEcosystemDocx } =
        await import("../../../utils/ecosystemDocxGenerator.js");

      await generateEcosystemDocx(reportData, {
        name: currentAnalysis?.name || "Analyse EBIOS RM",
        companyName: currentContextData?.organizationName || "Organisation",
      }, "en");

      showSuccessToast("Word Report (EN) generated successfully");
    } catch (error) {
      console.error("Error exporting DOCX (EN):", error);
      showErrorToast(`Error exporting Word (EN): ${error.message}`);
    }
  };

  // Handle French CSV export
  const handleExportCsvFr = () => {
    const generator = new Workshop3Activite1CsvGenerator(
      {
        stakeholders: partPrenantes,
        organizationName:
          currentContextData?.organizationName || "Organisation",
      },
      "fr",
    );
    generator.download(
      `EBIOS-RM_Workshop3_Activite1_${currentContextData?.organizationName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
    );
    showSuccessToast("Export CSV (FR) généré avec succès");
  };

  // Handle English CSV export
  const handleExportCsvEn = () => {
    const generator = new Workshop3Activite1CsvGenerator(
      {
        stakeholders: partPrenantes,
        organizationName:
          currentContextData?.organizationName || "Organisation",
      },
      "en",
    );
    generator.download(
      `EBIOS-RM_Workshop3_Activity1_${currentContextData?.organizationName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
    );
    showSuccessToast("CSV Export (EN) generated successfully");
  };

  // Filter stakeholders based on current filters
  const filteredStakeholders = partPrenantes.filter((stakeholder) => {
    const typeMatch = filterType === "all" || stakeholder.type === filterType;
    const categoryMatch =
      filterCategory === "all" || stakeholder.category === filterCategory;
    return typeMatch && categoryMatch;
  });

  return (
    <div
      ref={formChangeRef}
      className="space-y-6 p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen"
    >
      {/* === MERGED HEADER === */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Left Side: Breadcrumb & Title */}
          <div>
            <div className="flex items-center text-sm text-slate-500 mb-2">
              <span className="hover:text-blue-600 transition-colors">
                {t("workshop3.title")}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mx-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span className="text-blue-600 font-medium">
                {t("workshop3.activity1.breadcrumb")}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
              <Users size={28} className="mr-3 text-blue-600" />
              {t("workshop3.activity1.title")}
            </h1>
          </div>

          {/* Right Side: Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Unified Export Menu */}
            <WorkshopExportMenu
              onExportPdfFr={handleExportPDF}
              onExportPdfEn={handleExportPDFEn}
              onExportDocxFr={handleExportDocx}
              onExportDocxEn={handleExportDocxEn}
              onExportCsvFr={handleExportCsvFr}
              onExportCsvEn={handleExportCsvEn}
            />

            {/* Save Button */}
            <Button
              variant="primary"
              color="blue"
              icon={<Save />}
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isLoading}
              title={t("common.save")}
            >
              {t("common.save")}
            </Button>

            {/* Help Button */}
            <Button
              variant="secondary"
              color="white"
              icon={<Info />}
              onClick={() => setIsGuideOpen(true)}
              title={t("common.help")}
            >
              {t("common.help")}
            </Button>
          </div>
        </div>
      </div>
      {/* === END MERGED HEADER === */}

      {/* Guide Modal - positioned right under the header */}
      <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      {/* Add stakeholder button and filters */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4 bg-gray-50 p-3 rounded-lg">
          <div className="flex items-center">
            <Filter size={16} className="text-gray-500 mr-2" />
            <span className="text-sm text-gray-600 mr-2">
              {t("workshop3.activity1.filters")}:
            </span>
          </div>
          <div className="relative">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="text-sm border border-gray-300 rounded-md pl-2 pr-7 py-1 appearance-none bg-none bg-white cursor-pointer"
              style={{ backgroundImage: 'none' }}
            >
              <option value="all">
                {t("workshop3.activity1.filter.allTypes")}
              </option>
              <option value="internal">
                {t("workshop3.activity1.filter.internal")}
              </option>
              <option value="external">
                {t("workshop3.activity1.filter.external")}
              </option>
            </select>
            <div className="absolute right-1.5 top-0 h-full flex items-center pointer-events-none text-gray-500">
              <ChevronDown size={14} />
            </div>
          </div>
          <div className="relative">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="text-sm border border-gray-300 rounded-md pl-2 pr-7 py-1 appearance-none bg-none bg-white cursor-pointer"
              style={{ backgroundImage: 'none' }}
            >
              <option value="all">
                {t("workshop3.activity1.filter.allCategories")}
              </option>
              {stakeholderCategories
                .filter((c) => c.id !== "unsorted")
                .map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name || t(`workshop3.activity1.categories.${cat.id}`)}
                  </option>
                ))}
            </select>
            <div className="absolute right-1.5 top-0 h-full flex items-center pointer-events-none text-gray-500">
              <ChevronDown size={14} />
            </div>
          </div>
        </div>
        <Button
          variant="primary"
          color="blue"
          icon={<Plus />}
          onClick={() => {
            setSelectedStakeholder(null);
            setIsFormOpen(true);
          }}
          title={t("workshop3.activity1.addStakeholder")}
        >
          {t("workshop3.activity1.addStakeholder")}
        </Button>
      </div>

      {/* Category manager modal (opened from the stakeholder form) */}
      {isCategoryManagerOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
            onClick={() => setIsCategoryManagerOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-end p-3 border-b border-gray-200">
                <button
                  onClick={() => setIsCategoryManagerOpen(false)}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                  title={t("common.close", "Fermer")}
                >
                  <X size={18} />
                </button>
              </div>
              <CategoryManager
                embedded
                stakeholders={stakeholders}
                onAddCategory={handleAddCategory}
                onRenameCategory={handleRenameCategory}
                onDeleteCategory={handleDeleteCategory}
              />
            </div>
          </div>,
          document.body,
        )}

      {/* Main content with three-column layout with separation */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {" "}
        {/* Using 12 columns with gap for separation */}
        {/* Left column - Stakeholder Cards (5/12 width) */}
        <div className="lg:col-span-5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700 flex items-center">
              <Users size={18} className="mr-2 text-blue-600" />{" "}
              {/* Changed icon to Users for cards */}
              {t("workshop3.activity1.stakeholders")}
            </h3>
          </div>
          <div className="max-h-[600px] overflow-auto p-1">
            <StakeholderTable
              stakeholders={filteredStakeholders}
              categoryNamesById={categoryNamesById}
              onEdit={(stakeholder) => {
                setSelectedStakeholder(stakeholder);
                setIsFormOpen(true);
              }}
              onDelete={handleDeleteStakeholder}
              onToggleRetained={handleToggleRetained}
              onToggleCritical={handleToggleCritical}
              onUpdateCriticalJustification={handleUpdateCriticalJustification}
              isLoading={isLoading}
            />
          </div>
        </div>
        {/* Middle and Right columns - Legend and Visualization OR Form */}
        {isFormOpen ? (
          // When form is open, it takes the full width of both legend and visualization columns
          <div className="lg:col-span-7 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h3 className="font-medium text-gray-700 flex items-center">
                <Users size={18} className="mr-2 text-blue-600" />
                {selectedStakeholder
                  ? t("workshop3.activity1.editStakeholder")
                  : t("workshop3.activity1.addStakeholder")}
              </h3>
            </div>
            <div className="max-h-[600px] overflow-auto">
              <StakeholderForm
                stakeholder={selectedStakeholder}
                categories={stakeholderCategories}
                onSubmit={handleStakeholderSubmit}
                onOpenCategoryManager={() => setIsCategoryManagerOpen(true)}
                onCancel={() => {
                  setSelectedStakeholder(null);
                  setIsFormOpen(false);
                }}
              />
            </div>
          </div>
        ) : (
          // When form is not open, show both legend and visualization columns
          <>
            {/* Middle column - Vertical Legend (2/12 width) */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-700 flex items-center justify-center">
                  <Info size={16} className="mr-2 text-blue-600" />
                  {t("workshop3.activity1.legend.title")}
                </h3>
              </div>
              <div className="p-3 space-y-4">
                {/* Zones section */}
                <div>
                  <div className="font-medium text-xs mb-2 text-gray-700">
                    {t("workshop3.activity1.legend.zones")}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-red-100 border border-red-400 mr-2"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.danger")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-amber-100 border border-amber-400 mr-2"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.control")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-100 border border-green-400 mr-2"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.watch")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Types section */}
                <div>
                  <div className="font-medium text-xs mb-2 text-gray-700">
                    {t("workshop3.activity1.legend.types")}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-slate-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.filter.external")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 bg-slate-500 mr-2"
                        style={{ transform: "rotate(45deg)" }}
                      ></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.filter.internal")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Fiabilité Cyber section */}
                <div>
                  <div className="font-medium text-xs mb-2 text-gray-700">
                    {t(
                      "workshop3.activity1.legend.cyberReliability",
                      "Fiabilité Cyber",
                    )}{" "}
                    (Maturité × Confiance)
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: "#EF4444" }}
                      ></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.weak", "Faible")} (&lt;
                        4)
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: "#F97316" }}
                      ></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.average", "Moyenne")}{" "}
                        (4-5)
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: "#3B82F6" }}
                      ></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.good", "Bonne")} (6-7)
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: "#10B981" }}
                      ></div>
                      <span className="text-xs text-gray-600">
                        {t(
                          "workshop3.activity1.legend.excellent",
                          "Excellente",
                        )}{" "}
                        (&gt; 7)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Exposition section */}
                <div>
                  <div className="font-medium text-xs mb-2 text-gray-700">
                    {t("workshop3.activity1.legend.exposure", "Exposition")}{" "}
                    (Dépendance × Pénétration)
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full mr-2 border border-gray-400 bg-gray-100"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.minimal", "Minimal")}{" "}
                        (&lt; 3)
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full mr-2 border border-gray-400 bg-gray-100"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.moderate", "Moderate")}{" "}
                        (3-6)
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-5 h-5 rounded-full mr-2 border border-gray-400 bg-gray-100"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.high", "High")} (7-9)
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded-full mr-2 border border-gray-400 bg-gray-100"></div>
                      <span className="text-xs text-gray-600">
                        {t("workshop3.activity1.legend.critical", "Critical")}{" "}
                        (&gt; 9)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column - Visualization (5/12 width) */}
            <div className="lg:col-span-5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <h3 className="font-medium text-gray-700 flex items-center">
                  <BarChart2 size={18} className="mr-2 text-blue-600" />
                  {t("workshop3.activity1.radarVisualization")}
                </h3>
                <button
                  onClick={() => setIsThresholdEditing(!isThresholdEditing)}
                  className="p-2 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 flex items-center text-xs"
                  title={t("workshop3.activity1.modifyThresholds")}
                >
                  <Settings size={16} className="text-gray-600 mr-1" />
                  {t("workshop3.activity1.modifyThresholds")}
                </button>
              </div>
              <div className="max-h-[600px] overflow-auto">
                <ThreatRadarVisualization
                  ref={threatRadarRef}
                  stakeholders={filteredStakeholders}
                  thresholds={thresholds}
                  onUpdateThresholds={handleUpdateThresholds}
                  onSelectStakeholder={(stakeholder) => {
                    setSelectedStakeholder(stakeholder);
                    setIsFormOpen(true);
                  }}
                  isLoading={isLoading}
                  isThresholdEditing={isThresholdEditing}
                  setIsThresholdEditing={setIsThresholdEditing}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Summary table - bottom of the tab */}
      <StakeholderSummaryTable stakeholders={partPrenantes} categoryNamesById={categoryNamesById} />
    </div>
  );
;
};

export default Activite1;
